import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';
import { Plus, Trash2, Zap, Clock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { addMonths, format, parseISO } from 'date-fns';
import {
  calcVestingPosition,
  vestingAccelerationApplies,
  vestingHorizonMonths,
} from '@/utils/equityMath';
import type { VestingAccelerationType, VestingGrantTerms } from '@/utils/equityMath';

interface VestingGrant {
  id: string;
  name: string;
  role: string;
  type: 'founder' | 'employee' | 'advisor';
  totalShares: number;
  startDate: string;
  cliffMonths: number;
  vestingMonths: number;
  accelerationType: VestingAccelerationType;
  accelerationPct: number;
}

/** The shape the maths works in: dates rather than ISO strings. */
const termsOf = (grant: VestingGrant): VestingGrantTerms => ({
  totalShares: grant.totalShares,
  startDate: parseISO(grant.startDate),
  cliffMonths: grant.cliffMonths,
  vestingMonths: grant.vestingMonths,
  accelerationType: grant.accelerationType,
  accelerationPct: grant.accelerationPct,
});

/**
 * Chart series are keyed by grant id, not by name. Keying by name meant two
 * grants sharing one — two "Engineer" rows, say — collapsed into a single
 * series, and one of them silently disappeared from the timeline.
 */
const seriesKey = (grant: VestingGrant) => `grant_${grant.id}`;

export default function VestingScheduleSimulator() {
  const [grants, setGrants] = useState<VestingGrant[]>([
    {
      id: '1',
      name: 'Founder 1',
      role: 'CEO',
      type: 'founder',
      totalShares: 2500000,
      startDate: '2023-01-01',
      cliffMonths: 12,
      vestingMonths: 48,
      accelerationType: 'double',
      accelerationPct: 100,
    },
    {
      id: '2',
      name: 'Engineer 1',
      role: 'Senior Engineer',
      type: 'employee',
      totalShares: 50000,
      startDate: '2023-06-01',
      cliffMonths: 12,
      vestingMonths: 48,
      accelerationType: 'single',
      accelerationPct: 50,
    },
  ]);

  const [newGrant, setNewGrant] = useState<Omit<VestingGrant, 'id'>>({
    name: '',
    role: '',
    type: 'employee',
    totalShares: 0,
    startDate: format(new Date(), 'yyyy-MM-dd'),
    cliffMonths: 12,
    vestingMonths: 48,
    accelerationType: 'none',
    accelerationPct: 0,
  });

  const [simulationDate, setSimulationDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [exitEvent, setExitEvent] = useState(false);
  const [terminatedWithoutCause, setTerminatedWithoutCause] = useState(false);

  const addGrant = () => {
    if (newGrant.name && newGrant.totalShares > 0) {
      setGrants([
        ...grants,
        {
          id: Date.now().toString(),
          ...newGrant,
        },
      ]);
      setNewGrant({
        name: '',
        role: '',
        type: 'employee',
        totalShares: 0,
        startDate: format(new Date(), 'yyyy-MM-dd'),
        cliffMonths: 12,
        vestingMonths: 48,
        accelerationType: 'none',
        accelerationPct: 0,
      });
    }
  };

  const removeGrant = (id: string) => {
    setGrants(grants.filter((g) => g.id !== id));
  };

  const calculateVesting = (
    grant: VestingGrant,
    asOfDate: string,
    withAcceleration: boolean = false
  ) => calcVestingPosition(termsOf(grant), parseISO(asOfDate), withAcceleration);

  const shouldAccelerate = (grant: VestingGrant) =>
    vestingAccelerationApplies(grant.accelerationType, {
      exitEvent,
      terminatedWithoutCause,
    });

  const currentVesting = grants.map((grant) => {
    const position = calculateVesting(grant, simulationDate, shouldAccelerate(grant));
    return {
      grant,
      ...position,
      accelerationApplied: shouldAccelerate(grant),
    };
  });

  const grantWarnings = [...new Set(currentVesting.flatMap((cv) => cv.warnings))];

  // Monthly snapshots running to the point where the last grant has finished
  // vesting, rather than a fixed 60 months from the earliest start.
  const generateTimeline = () => {
    if (grants.length === 0) return [];

    const starts = grants.map((g) => parseISO(g.startDate));
    const earliestStart = starts.reduce((a, b) => (b < a ? b : a));
    const months = vestingHorizonMonths(grants.map(termsOf), earliestStart);

    return Array.from({ length: months + 1 }, (_, i) => {
      const snapshotDate = addMonths(earliestStart, i);
      const dateStr = format(snapshotDate, 'yyyy-MM-dd');
      const dataPoint: Record<string, string | number> = {
        date: format(snapshotDate, 'MMM yyyy'),
      };
      for (const grant of grants) {
        dataPoint[seriesKey(grant)] = calculateVesting(grant, dateStr, false).vested;
      }
      return dataPoint;
    });
  };

  const timelineData = generateTimeline();

  const totalVested = currentVesting.reduce((sum, cv) => sum + cv.vested, 0);
  const totalUnvested = currentVesting.reduce((sum, cv) => sum + cv.unvested, 0);
  const totalShares = grants.reduce((sum, g) => sum + g.totalShares, 0);
  const totalVestedPct = totalShares > 0 ? (totalVested / totalShares) * 100 : 0;

  const colors = [
    'hsl(var(--primary))',
    'hsl(var(--secondary))',
    '#8884d8',
    '#82ca9d',
    '#ffc658',
    '#ff7c7c',
    '#a78bfa',
    '#fb923c',
  ];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Vesting Schedule Simulator
          </CardTitle>
          <CardDescription>
            Model 4-year vesting schedules with cliff periods and acceleration scenarios
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Add New Grant */}
          <div className="space-y-4">
            <h3 className="font-semibold">Add Vesting Grant</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label>Name</Label>
                <Input
                  value={newGrant.name}
                  onChange={(e) => setNewGrant({ ...newGrant, name: e.target.value })}
                  placeholder="Employee name"
                />
              </div>
              <div>
                <Label>Role</Label>
                <Input
                  value={newGrant.role}
                  onChange={(e) => setNewGrant({ ...newGrant, role: e.target.value })}
                  placeholder="Position"
                />
              </div>
              <div>
                <Label>Type</Label>
                <Select
                  value={newGrant.type}
                  onValueChange={(value: VestingGrant['type']) =>
                    setNewGrant({ ...newGrant, type: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="founder">Founder</SelectItem>
                    <SelectItem value="employee">Employee</SelectItem>
                    <SelectItem value="advisor">Advisor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Total Shares</Label>
                <Input
                  type="number"
                  value={newGrant.totalShares || ''}
                  min="0"
                  onChange={(e) =>
                    setNewGrant({ ...newGrant, totalShares: Math.max(0, Number(e.target.value)) })
                  }
                  placeholder="50000"
                />
              </div>
              <div>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={newGrant.startDate}
                  onChange={(e) => setNewGrant({ ...newGrant, startDate: e.target.value })}
                />
              </div>
              <div>
                <Label>Cliff (Months)</Label>
                <Input
                  type="number"
                  value={newGrant.cliffMonths || ''}
                  min="0"
                  onChange={(e) =>
                    setNewGrant({ ...newGrant, cliffMonths: Math.max(0, Number(e.target.value)) })
                  }
                  placeholder="12"
                />
              </div>
              <div>
                <Label>Vesting Period (Months)</Label>
                <Input
                  type="number"
                  value={newGrant.vestingMonths || ''}
                  min="0"
                  onChange={(e) =>
                    setNewGrant({ ...newGrant, vestingMonths: Math.max(0, Number(e.target.value)) })
                  }
                  placeholder="48"
                />
              </div>
              <div>
                <Label>Acceleration</Label>
                <Select
                  value={newGrant.accelerationType}
                  onValueChange={(value: VestingAccelerationType) =>
                    setNewGrant({ ...newGrant, accelerationType: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="single">Single Trigger</SelectItem>
                    <SelectItem value="double">Double Trigger</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Acceleration %</Label>
                <Input
                  type="number"
                  value={newGrant.accelerationPct || ''}
                  min="0"
                  max="100"
                  onChange={(e) =>
                    setNewGrant({
                      ...newGrant,
                      accelerationPct: Math.min(100, Math.max(0, Number(e.target.value))),
                    })
                  }
                  placeholder="100"
                />
              </div>
            </div>
            <Button onClick={addGrant} className="w-full">
              <Plus className="h-4 w-4 mr-2" />
              Add Grant
            </Button>
          </div>

          <Separator />

          {/* Current Grants */}
          <div className="space-y-2">
            <h3 className="font-semibold">Current Grants</h3>
            {grants.map((grant) => (
              <div
                key={grant.id}
                className="flex items-center justify-between p-3 border rounded-lg"
              >
                <div className="flex items-center gap-4 flex-wrap">
                  <Badge variant="outline">{grant.type}</Badge>
                  <div>
                    <div className="font-medium">{grant.name}</div>
                    <div className="text-sm text-muted-foreground">{grant.role}</div>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {grant.totalShares.toLocaleString()} shares • {grant.vestingMonths}mo vesting •{' '}
                    {grant.cliffMonths}
                    mo cliff
                  </div>
                  {grant.accelerationType !== 'none' && (
                    <Badge variant="secondary">
                      <Zap className="h-3 w-3 mr-1" />
                      {grant.accelerationType === 'single' ? 'Single' : 'Double'} Trigger (
                      {grant.accelerationPct}%)
                    </Badge>
                  )}
                </div>
                <Button variant="ghost" size="sm" onClick={() => removeGrant(grant.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>

          <Separator />

          {/* Simulation Controls */}
          <div className="space-y-4">
            <h3 className="font-semibold">Simulation Parameters</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Simulation Date</Label>
                <Input
                  type="date"
                  value={simulationDate}
                  onChange={(e) => setSimulationDate(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="exitEvent"
                  checked={exitEvent}
                  onChange={(e) => setExitEvent(e.target.checked)}
                  className="h-4 w-4"
                />
                <Label htmlFor="exitEvent" className="cursor-pointer">
                  Exit Event (Acquisition/IPO)
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="terminatedWithoutCause"
                  checked={terminatedWithoutCause}
                  onChange={(e) => setTerminatedWithoutCause(e.target.checked)}
                  className="h-4 w-4"
                />
                <Label htmlFor="terminatedWithoutCause" className="cursor-pointer">
                  Termination w/o Cause
                </Label>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {grants.length > 0 && (
        <>
          {/* Summary Metrics */}
          <Card>
            <CardHeader>
              <CardTitle>Vesting Summary</CardTitle>
              <CardDescription>As of {format(parseISO(simulationDate), 'PPP')}</CardDescription>
            </CardHeader>
            <CardContent>
              {grantWarnings.length > 0 && (
                <div className="mb-6 space-y-2">
                  {grantWarnings.map((warning, i) => (
                    <div
                      key={i}
                      className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg"
                    >
                      <p className="text-sm text-muted-foreground">{warning}</p>
                    </div>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="p-4 border rounded-lg">
                  <div className="text-sm text-muted-foreground mb-1">Total Vested</div>
                  <div className="text-2xl font-bold">{totalVested.toLocaleString()}</div>
                  <div className="text-sm text-muted-foreground">
                    {totalVestedPct.toFixed(1)}% of total
                  </div>
                </div>
                <div className="p-4 border rounded-lg">
                  <div className="text-sm text-muted-foreground mb-1">Total Unvested</div>
                  <div className="text-2xl font-bold">{totalUnvested.toLocaleString()}</div>
                  <div className="text-sm text-muted-foreground">
                    {((totalUnvested / totalShares) * 100).toFixed(1)}% remaining
                  </div>
                </div>
                <div className="p-4 border rounded-lg">
                  <div className="text-sm text-muted-foreground mb-1">Total Grants</div>
                  <div className="text-2xl font-bold">{grants.length}</div>
                </div>
                <div className="p-4 border rounded-lg">
                  <div className="text-sm text-muted-foreground mb-1">Acceleration Active</div>
                  <div className="text-2xl font-bold">
                    {currentVesting.filter((cv) => cv.accelerationApplied).length}
                  </div>
                </div>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead className="text-right">Total Shares</TableHead>
                    <TableHead className="text-right">Vested</TableHead>
                    <TableHead className="text-right">Unvested</TableHead>
                    <TableHead>Progress</TableHead>
                    <TableHead>Acceleration</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentVesting.map((cv) => (
                    <TableRow key={cv.grant.id}>
                      <TableCell className="font-medium">{cv.grant.name}</TableCell>
                      <TableCell>{cv.grant.role}</TableCell>
                      <TableCell className="text-right">
                        {cv.grant.totalShares.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right">{cv.vested.toLocaleString()}</TableCell>
                      <TableCell className="text-right">{cv.unvested.toLocaleString()}</TableCell>
                      <TableCell>
                        <Badge variant={cv.vestedPct >= 100 ? 'default' : 'secondary'}>
                          {cv.vestedPct.toFixed(1)}%
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {cv.accelerationApplied ? (
                          <Badge variant="default" className="gap-1">
                            <Zap className="h-3 w-3" />+{cv.acceleratedShares.toLocaleString()}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-sm">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Vesting Timeline Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Vesting Timeline</CardTitle>
              <CardDescription>Projected vesting schedule over time</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={timelineData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis label={{ value: 'Vested Shares', angle: -90, position: 'insideLeft' }} />
                  <Tooltip />
                  <Legend />
                  {grants.map((grant, idx) => (
                    <Line
                      key={grant.id}
                      type="stepAfter"
                      dataKey={seriesKey(grant)}
                      name={grant.name || `Grant ${idx + 1}`}
                      stroke={colors[idx % colors.length]}
                      strokeWidth={2}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Acceleration Scenarios */}
          <Card>
            <CardHeader>
              <CardTitle>Acceleration Impact Analysis</CardTitle>
              <CardDescription>
                Compare vesting with and without acceleration triggers
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Acceleration Type</TableHead>
                    <TableHead className="text-right">Vested (No Accel)</TableHead>
                    <TableHead className="text-right">Vested (With Accel)</TableHead>
                    <TableHead className="text-right">Additional Shares</TableHead>
                    <TableHead>Benefit</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {grants
                    .filter((g) => g.accelerationType !== 'none')
                    .map((grant) => {
                      const withoutAccel = calculateVesting(grant, simulationDate, false);
                      const withAccel = calculateVesting(grant, simulationDate, true);
                      const additionalShares = withAccel.vested - withoutAccel.vested;
                      const benefitPct =
                        grant.totalShares > 0 ? (additionalShares / grant.totalShares) * 100 : 0;

                      return (
                        <TableRow key={grant.id}>
                          <TableCell className="font-medium">{grant.name}</TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {grant.accelerationType === 'single' ? 'Single' : 'Double'} (
                              {grant.accelerationPct}%)
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {withoutAccel.vested.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right">
                            {withAccel.vested.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right text-primary font-medium">
                            +{additionalShares.toLocaleString()}
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">{benefitPct.toFixed(1)}% of grant</Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>

              {grants.filter((g) => g.accelerationType !== 'none').length === 0 && (
                <p className="text-center text-muted-foreground py-8">
                  No grants with acceleration clauses
                </p>
              )}
            </CardContent>
          </Card>

          {/* Strategy Insights */}
          <Card>
            <CardHeader>
              <CardTitle>Vesting Strategy Insights</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {totalVestedPct < 25 && (
                <div className="p-3 bg-secondary/10 border border-secondary/20 rounded-lg">
                  <p className="text-sm font-medium">📊 Early Stage Vesting</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Only {totalVestedPct.toFixed(1)}% of equity has vested. Retention risk is high
                    if key team members leave before cliff periods complete.
                  </p>
                </div>
              )}
              {grants.some((g) => g.accelerationType === 'single') && (
                <div className="p-3 bg-primary/10 border border-primary/20 rounded-lg">
                  <p className="text-sm font-medium">💡 Single-Trigger Acceleration</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Some grants have single-trigger acceleration. This means shares accelerate on
                    exit alone, which may not align with buyer retention goals. Consider negotiating
                    for double-trigger.
                  </p>
                </div>
              )}
              {currentVesting.some((cv) => cv.vestedPct > 90) && (
                <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                  <p className="text-sm font-medium">⚠️ Nearly Fully Vested</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Some team members are over 90% vested. Consider refreshing grants to maintain
                    retention incentives for key employees.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
